import { useCallback, type CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Gamepad2, Wallet, Receipt } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDeviceMode } from '@/contexts/DeviceModeContext';
import { useAuthModal } from '@/contexts/AuthModalContext';
import { playClickSound } from '@/utils/soundUtils';

const TABS = [
  { id: 'home', route: '/', icon: Home, label: 'header.tabs.home', fallback: 'Home', gated: false },
  { id: 'games', route: '/account/games', icon: Gamepad2, label: 'header.tabs.games', fallback: 'Games', gated: true },
  { id: 'wallet', route: '/account/finance/wallet', icon: Wallet, label: 'header.tabs.wallet', fallback: 'Wallet', gated: true },
  { id: 'activity', route: '/account/transactions', icon: Receipt, label: 'header.tabs.activity', fallback: 'Activity', gated: true },
] as const;

function activeTab(pathname: string): string {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/account/games')) return 'games';
  if (pathname.startsWith('/account/transactions')) return 'activity';
  if (pathname.startsWith('/account/finance') || pathname.startsWith('/account/wallet')) return 'wallet';
  return '';
}

export default function BottomNav() {
  const mode = useDeviceMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { requireAuth } = useAuthModal();
  const active = activeTab(location.pathname);
  const activeIndex = TABS.findIndex((tab) => tab.id === active);

  const go = useCallback(
    (route: string, gated: boolean) => {
      playClickSound();
      if (gated) requireAuth(() => navigate(route));
      else navigate(route);
    },
    [navigate, requireAuth]
  );

  if (mode !== 'mobile') return null;

  return (
    <nav
      className="bottom-nav relative z-[var(--wf-z-bottom-nav)] shrink-0 w-full flex items-stretch border-t border-border-ice bg-space-top/95 backdrop-blur-md safe-area-bottom"
      role="navigation"
      aria-label={t('nav.bottom', 'Primary')}
      style={{ '--bn-count': TABS.length, '--bn-index': activeIndex } as CSSProperties}
    >
      {activeIndex >= 0 && <span className="bottom-nav__indicator" aria-hidden />}
      {TABS.map(({ id, route, icon: Icon, label, fallback, gated }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => go(route, gated)}
            aria-current={isActive ? 'page' : undefined}
            className={`bottom-nav__tab flex-1 min-h-[var(--wf-bottom-nav-h,56px)] flex flex-col items-center justify-center gap-1 focus-visible:outline-none focus-visible:shadow-focus-ring ${
              isActive ? 'text-violet-brand is-active' : 'text-caption-muted'
            }`}
          >
            <Icon size={24} aria-hidden />
            <span className="bottom-nav__label leading-none">{t(label, fallback)}</span>
          </button>
        );
      })}
    </nav>
  );
}
